import { Link } from 'react-router-dom';
import { ArrowRight, Mail, School, ShieldCheck, Clock, BarChart3 } from 'lucide-react';

const CONTACT_EMAIL = 'contact@xed21.com';

const benefits = [
  {
    icon: BarChart3,
    title: 'Consistent quality across departments',
    description:
      "Every teacher generates Bloom's-tagged, NCERT-aligned questions. Every paper meets the same standard — without manual review cycles.",
    color: 'bg-teal-50 text-teal-600',
  },
  {
    icon: Clock,
    title: 'Exam cycles in days, not weeks',
    description:
      'Question papers in minutes instead of weeks of back-and-forth between teachers and coordinators. No more last-minute submissions.',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    icon: ShieldCheck,
    title: 'No institutional commitment',
    description:
      'No annual contract. No subscription. No minimum spend. Teachers try it with free wallet credit. Top up only when it works.',
    color: 'bg-emerald-50 text-emerald-600',
  },
];

export default function ForSchoolsSection() {
  return (
    <section id="for-schools" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <School className="w-3.5 h-3.5" />
            For schools and institutions
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Built for Schools,{' '}
            <span className="text-teal-600">Not Just Individual Teachers</span>
          </h2>
          <p className="text-lg text-slate-500 mt-4">
            Academic coordinators and school leaders use Xed21 to standardize
            assessment quality across every teacher, subject, and exam cycle.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="bg-slate-50 rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300 group"
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${benefit.color} mb-4 group-hover:scale-110 transition-transform duration-300`}
              >
                <benefit.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 sm:p-10 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-white">
            Want to onboard your school?
          </h3>
          <p className="text-slate-400 mt-2 max-w-lg mx-auto">
            Get bulk wallet credit for your teachers, institutional billing,
            and dedicated onboarding support.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-all shadow-lg shadow-teal-700/25"
            >
              Contact Us for School Onboarding
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-2 px-6 py-3 border border-slate-600 text-slate-300 rounded-xl text-sm font-semibold hover:bg-slate-700 hover:text-white transition-all"
            >
              <Mail className="w-4 h-4" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
